import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { ExternalLink, Cloud, Code, Database, Shield, ArrowLeft, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import az900Badge from "@/assets/az-900_bage.png";
import az204Badge from "@/assets/az-204_bage.png";
// import az305Badge from "@/assets/az-305_badge.png";
import githubbage from "@/assets/githubbage.png";
import oracleBadgeAI from "@/assets/OAI.png";
import oracleBadgeInfra from "@/assets/OCI.png";
import Ovai from "@/assets/Ovai.png";
import oracleBadgeGenAI from "@/assets/ocgi.png";
import Meta from "@/assets/Meta.png";
import { useNavigate, useLocation } from "react-router-dom";

interface Certificate {
  id: string;
  title: string;
  issuer: string;
  date: string;
  image: string;
  link: string;
  category: "cloud" | "development" | "security" | "other" | "all";
  skills: string[];
}

const certificates: Certificate[] = [
  {
    id: "az-204",
    title: "Developing Solutions for Microsoft Azure",
    issuer: "Microsoft",
    date: "Jun 2025",
    image: az204Badge,
    link: "https://learn.microsoft.com/api/credentials/share/en-us/MohanBalaji-7174/732B4065AC26B8CE?sharingId=40E742C63BF39CC0",
    category: "cloud",
    skills: [
      "Azure SDKs",
      "Azure Functions",
      "App Services",
      "Azure Cosmos DB",
      "Blob Storage",
      "Azure CLI",
      "ARM Templates",
      "Azure Key Vault",
      "Azure Event Grid",
      "Azure Service Bus",
      "Monitoring",
      "CI/CD with GitHub Actions",
      "Authentication & Authorization"
    ]
  },  
  {
    id: "az-900",
    title: "Microsoft Azure Fundamentals",
    issuer: "Microsoft",
    date: "Feb 2025",
    image: az900Badge,
    link: "https://learn.microsoft.com/api/credentials/share/en-us/MohanBalaji-7174/4A60B073009EB146?sharingId=40E742C63BF39CC0",
    category: "cloud",
    skills: ["Azure", "Cloud Computing", "Virtual Machines", "Storage", "Networking"]
  },
  {
    id: "github-foundations",
    title: "GitHub Foundations",
    issuer: "GitHub",
    date: "March 2025",
    image: githubbage,
    link: "https://www.credly.com/badges/fbf7762c-2fe9-4bb6-8e73-2487cb8d4454/",
    category: "other",
    skills: ["Git", "Version Control", "CI/CD", "Collaboration", "DevOps"]
  },
  {
    id: "oracle-ai-vector",
    title: "Oracle AI Vector Search Certified Professional",
    issuer: "Oracle",
    date: "2025",
    image: Ovai,
    link: "https://catalog-education.oracle.com/ords/certview/sharebadge?id=C3D91D371DE159F3337A5795A81159FA299B77D8BC14F13F6121AEEF30AA6C3D",
    category: "cloud",
    skills: ["AI", "Vector Search", "Oracle Cloud", "Machine Learning"]
  },
  {
    id: "oracle-gen-ai",
    title: "Oracle Cloud Infrastructure 2025 Certified Generative AI Professional",
    issuer: "Oracle",
    date: "2025",
    image: oracleBadgeGenAI,
    link: "https://catalog-education.oracle.com/", // Update with Actual link
    category: "cloud",
    skills: ["Generative AI", "Oracle Cloud", "AI", "Machine Learning", "LLMs"]
  },
  {
    id: "oracle-ai-foundations",
    title: "Oracle Cloud Infrastructure AI Certified Foundations",
    issuer: "Oracle",
    date: "2025",
    image: oracleBadgeAI,
    link: "https://catalog-education.oracle.com/ords/certview/sharebadge?id=805B313D54435063BFE2FF5425D403B97820AF27B859254A4779725559215662",
    category: "cloud",
    skills: ["Oracle Cloud", "AI", "Machine Learning", "Cloud Infrastructure"]
  },
  {
    id: "oracle-cloud-infra",
    title: "Oracle Cloud Infrastructure 2024 Certified Foundations Associates",
    issuer: "Oracle",
    date: "2025",
    image: oracleBadgeInfra,
    link: "https://catalog-education.oracle.com/ords/certview/sharebadge?id=805B313D54435063BFE2FF5425D403B97820AF27B859254A4779725559215662",
    category: "cloud",
    skills: ["Oracle Cloud", "Infrastructure", "Networking", "Security"]
  },
  {
    id: "meta-frontend",
    title: "Introduction to Front-End Development",
    issuer: "Meta (via Coursera)",
    date: "2024",
    image: Meta,
    link: "https://www.coursera.org/account/accomplishments/verify/SJ63NFER6MY7",
    category: "development",
    skills: ["HTML", "CSS", "JavaScript", "React", "Web Development"]
  }
];

// Certificate Modal Component
const CertificateModal = ({ certificate, onClose }: { certificate: Certificate; onClose: () => void }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/85 p-3 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative my-auto w-full max-w-2xl max-h-[calc(100svh-1.5rem)] overflow-y-auto rounded-xl border border-gray-700/50 bg-gray-900 p-4 sm:p-6"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-foreground transition-colors p-2"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="flex flex-col gap-4 md:flex-row md:gap-6">
          <div className="flex w-full items-center justify-center rounded-lg bg-gray-800/50 p-3 md:w-1/2 md:p-4">
            <img
              src={certificate.image}
              alt={certificate.title}
              className="max-h-44 w-full object-contain rounded-lg sm:max-h-64"
            />
          </div>
          
          <div className="w-full md:w-1/2">
            <h2 className="mb-2 pr-8 text-xl font-bold leading-tight sm:text-2xl">{certificate.title}</h2>
            <p className="mb-3 text-gray-400">{certificate.issuer}</p>
            <p className="mb-4 text-gray-500">Issued: {certificate.date}</p>
            
            <div className="flex flex-wrap gap-2 mb-6">
              {certificate.skills.map((skill) => (
                <span
                  key={skill}
                  className="rounded-full bg-indigo-400/20 px-3 py-1 text-xs text-indigo-200 sm:text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
            
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <Button
                onClick={() => window.open(certificate.link, '_blank')}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-foreground hover:bg-purple-700 sm:w-auto"
              >
                Verify Certificate
              </Button>
              <Button
                onClick={() => {
                  navigate('/#projects');
                  onClose();
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-foreground hover:bg-gray-700 sm:w-auto"
              >
                View Portfolio
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CertificatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<"all" | "cloud" | "development" | "security" | "other">("all");
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);

  // Handle URL hash changes using react-router's location
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const certificate = certificates.find(cert => cert.id === hash);
      if (certificate) {
        setSelectedCertificate(certificate);
      }
    } else {
      setSelectedCertificate(null);
    }
  }, [location.hash]);

  const allSkills = Array.from(new Set(certificates.flatMap(cert => cert.skills)));

  const filteredCertificates = certificates.filter(cert => {
    const categoryMatch = selectedCategory === "all" || cert.category === selectedCategory;
    const skillMatch = !selectedSkill || cert.skills.includes(selectedSkill);
    return categoryMatch && skillMatch;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-black px-4 py-20 text-foreground sm:px-6 sm:py-24 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <Button
            onClick={() => navigate('/')}
            className="bg-black/40 hover:bg-black/60 text-foreground px-4 py-2 rounded-full flex items-center gap-2 transition-all duration-300 border border-border hover:border-border text-sm font-medium "
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portfolio
          </Button>
        </motion.div>

        <div className="mb-12 pt-6 text-center sm:mb-16 sm:pt-12 md:pt-16 lg:pt-20">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-4 text-3xl font-bold leading-tight bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent sm:text-5xl md:text-6xl"
          >
            Professional Certifications
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-base text-muted-foreground sm:text-xl"
          >
            Showcasing my expertise and achievements
          </motion.p>
        </div>

        <div className="mb-8 flex flex-col gap-5 sm:mb-12 sm:gap-6">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              onClick={() => {
                setSelectedCategory("all");
                setSelectedSkill(null);
              }}
              className="flex items-center gap-2 px-3 text-xs sm:px-4 sm:text-sm"
            >
              All Certificates
            </Button>
            <Button
              variant={selectedCategory === "cloud" ? "default" : "outline"}
              onClick={() => setSelectedCategory("cloud")}
              className="flex items-center gap-2 px-3 text-xs sm:px-4 sm:text-sm"
            >
              <Cloud className="h-4 w-4" />
              Cloud
            </Button>
            <Button
              variant={selectedCategory === "development" ? "default" : "outline"}
              onClick={() => setSelectedCategory("development")}
              className="flex items-center gap-2 px-3 text-xs sm:px-4 sm:text-sm"
            >
              <Code className="h-4 w-4" />
              Development
            </Button>
            <Button
              variant={selectedCategory === "security" ? "default" : "outline"}
              onClick={() => setSelectedCategory("security")}
              className="flex items-center gap-2 px-3 text-xs sm:px-4 sm:text-sm"
            >
              <Shield className="h-4 w-4" />
              Security
            </Button>
            <Button
              variant={selectedCategory === "other" ? "default" : "outline"}
              onClick={() => setSelectedCategory("other")}
              className="flex items-center gap-2 px-3 text-xs sm:px-4 sm:text-sm"
            >
              <Database className="h-4 w-4" />
              Other
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
            {allSkills.map((skill) => (
              <Button
                key={skill}
                variant={selectedSkill === skill ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedSkill(selectedSkill === skill ? null : skill)}
                className="h-8 px-2.5 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                {skill}
              </Button>
            ))}
          </div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8"
        >
          {filteredCertificates.map((cert) => (
            <motion.div
              key={cert.title}
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card className="bg-gray-900/50  border border-gray-800 overflow-hidden group h-full">
                <div className="relative h-40 overflow-hidden bg-gray-800/50 sm:h-48">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="w-full h-full object-contain p-4 transform group-hover:-translate-y-0.5 transition-transform duration-500"
                  />
                </div>
                
                <div className="flex h-auto flex-col p-4 sm:h-[calc(100%-12rem)] sm:p-6">
                  <h3 className="mb-2 break-words text-lg font-semibold transition-colors group-hover:text-primary sm:text-xl">
                    {cert.title}
                  </h3>
                  <p className="mb-3 text-sm text-gray-400 sm:mb-4 sm:text-base">{cert.issuer}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {cert.skills.map((skill) => (
                      <span
                        key={skill}
                        className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mt-2 flex items-center justify-between gap-3 sm:mt-auto">
                    <div className="text-xs text-gray-400 sm:text-sm">
                      Issued: {cert.date}
                    </div>
                    <Button
                      onClick={() => {
                        setSelectedCertificate(cert);
                        navigate(`#${cert.id}`);
                      }}
                      className="flex shrink-0 items-center gap-2 rounded-full border border-border bg-black/40 px-3 py-2 text-xs font-medium text-foreground transition-all duration-300 hover:border-border hover:bg-black/60 sm:px-4 sm:text-sm"
                    >
                      View
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {selectedCertificate && (
          <CertificateModal
            certificate={selectedCertificate}
            onClose={() => {
              setSelectedCertificate(null);
              navigate('/certificate', { replace: true }); // clear hash without adding history
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CertificatePage; 
